import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * オペレーター情報
 */
export interface Operator {
  id: string;
  name: string;
  permissions: {
    canCreateSchedule: boolean;
    canUpdateSchedule: boolean;
    canCreateWorkHistory: boolean;
    canUpdateWorkHistory: boolean;
    canViewReports: boolean;
    canDeleteData: boolean;
    canModifySettings: boolean;
  };
}

/**
 * オペレーションストアの状態
 */
interface OperationState {
  // 状態
  isOperationMode: boolean;
  currentOperator: Operator | null;
  sessionToken: string | null;
  sharedUserId: string | null;

  // アクション
  setOperationMode: (
    operator: Operator,
    sessionToken: string,
    sharedUserId: string
  ) => void;
  clearOperationMode: () => void;
  updateSessionToken: (token: string) => void;
  updateOperator: (operator: Partial<Operator>) => void;

  // 権限チェック用ヘルパー
  hasPermission: (permission: keyof Operator['permissions']) => boolean;
}

/**
 * オペレーションモード用のZustandストア
 * オペレーターの認証情報とセッション管理
 */
export const useOperationStore = create<OperationState>()(
  persist(
    (set, get) => ({
      // 初期状態
      isOperationMode: false,
      currentOperator: null,
      sessionToken: null,
      sharedUserId: null,

      // オペレーションモード開始
      setOperationMode: (operator, sessionToken, sharedUserId) => {
        set({
          isOperationMode: true,
          currentOperator: operator,
          sessionToken,
          sharedUserId,
        });
      },

      // オペレーションモード終了
      clearOperationMode: () => {
        set({
          isOperationMode: false,
          currentOperator: null,
          sessionToken: null,
          sharedUserId: null,
        });
      },

      // セッショントークン更新
      updateSessionToken: (token) => {
        set({ sessionToken: token });
      },

      // オペレーター情報更新
      updateOperator: (operator) => {
        const current = get().currentOperator;
        if (current) {
          set({
            currentOperator: {
              ...current,
              ...operator,
            },
          });
        }
      },

      // 権限チェック
      hasPermission: (permission) => {
        const { currentOperator } = get();
        if (!currentOperator) return false;
        return currentOperator.permissions[permission] || false;
      },
    }),
    {
      name: 'operation-storage',
      // セキュリティのため、sessionTokenはlocalStorageに保存しない
      partialize: (state) => ({
        isOperationMode: state.isOperationMode,
        currentOperator: state.currentOperator,
        sharedUserId: state.sharedUserId,
        // sessionTokenは除外
      }),
    }
  )
);

/**
 * オペレーションモードかどうかをチェックするフック
 */
export function useIsOperationMode(): boolean {
  return useOperationStore((state) => state.isOperationMode);
}

/**
 * 現在のオペレーター情報を取得するフック
 */
export function useCurrentOperator(): Operator | null {
  return useOperationStore((state) => state.currentOperator);
}

/**
 * 権限チェック用フック
 */
export function useOperatorPermission(
  permission: keyof Operator['permissions']
): boolean {
  return useOperationStore((state) => state.hasPermission(permission));
}
